import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type { AppDomain } from '@/stores/appDomain'

export type AppNavigationItem = {
  title: string
  icon: string
  to?: string
  value: string
  show: boolean
  action?: string
  badge?: number
  children?: AppNavigationItem[]
  domains?: AppDomain[] | 'all'
  kind?: 'item' | 'section'
}

type NavigationInput = {
  isGuest: boolean
  domain: AppDomain
}

function matchesDomain(item: AppNavigationItem, domain: AppDomain): boolean {
  if (!item.domains || item.domains === 'all') {
    return true
  }
  return item.domains.includes(domain)
}

function visibleItems(items: AppNavigationItem[], domain: AppDomain): AppNavigationItem[] {
  return items.filter((item) => item.show && matchesDomain(item, domain))
}

export function shouldShowAppSidebar(
  userType: string,
  routeName: unknown,
  publicMarketing: unknown,
): boolean {
  return userType !== 'non-authenticated' && routeName !== 'home' && !publicMarketing
}

export function navigationItemMatchesRoute(
  item: AppNavigationItem,
  route: Pick<RouteLocationNormalizedLoaded, 'name' | 'path' | 'query'>,
): boolean {
  const routeName = route.name
  if (typeof routeName === 'string') {
    if (routeName === item.value) {
      return true
    }
    if (item.value === 'projects' && routeName === 'project') {
      return true
    }
    if (item.value === 'sagas' && routeName === 'saga') {
      return true
    }
    if (item.value === 'layouts' && routeName === 'layout-edit') {
      return true
    }
    if (item.value === 'community-groups' && routeName === 'community-group') {
      return true
    }
    if (
      item.value === 'progress' &&
      (routeName === 'writing-sessions' ||
        routeName === 'challenges' ||
        routeName === 'writing-feedback')
    ) {
      return true
    }
  }

  if (!item.to) {
    return false
  }

  const queryIndex = item.to.indexOf('?')
  if (queryIndex >= 0) {
    const path = item.to.slice(0, queryIndex)
    const params = new URLSearchParams(item.to.slice(queryIndex + 1))
    const chapterId = params.get('chapterId')
    if (chapterId) {
      const currentChapterId = route.query.chapterId
      const current = Array.isArray(currentChapterId) ? currentChapterId[0] : currentChapterId
      return route.path === path && current === chapterId
    }

    let queryMatches = true
    params.forEach((value, key) => {
      const current = route.query[key]
      const currentValue = Array.isArray(current) ? current[0] : current
      if (currentValue !== value) {
        queryMatches = false
      }
    })
    return route.path === path && queryMatches
  }

  return route.path === item.to || route.path.startsWith(`${item.to}/`)
}

export function buildAppNavigationItems(input: NavigationInput): AppNavigationItem[] {
  const { isGuest, domain } = input
  const activeDomain = isGuest ? 'write' : domain

  const items: AppNavigationItem[] = [
    {
      title: 'Mi perfil',
      icon: 'mdi-account-circle-outline',
      to: '/profile',
      value: 'profile',
      show: isGuest,
      domains: ['write'],
    },
    {
      title: 'Mis proyectos',
      icon: 'mdi-folder-multiple',
      to: '/projects',
      value: 'projects',
      show: true,
      domains: ['write'],
    },
    {
      title: 'Mis sagas',
      icon: 'mdi-bookshelf',
      to: '/sagas',
      value: 'sagas',
      show: true,
      domains: ['write'],
    },
    {
      title: 'Maquetación',
      icon: 'mdi-layers-triple',
      to: '/layouts',
      value: 'layouts',
      show: true,
      domains: ['write'],
    },
    {
      title: 'Mis imágenes',
      icon: 'mdi-image-multiple',
      to: '/images',
      value: 'images',
      show: true,
      domains: ['write'],
    },
    {
      title: 'Mi Progreso',
      icon: 'mdi-star-shooting',
      to: '/progress',
      value: 'progress',
      show: true,
      domains: ['write'],
    },
    {
      title: 'En este dispositivo',
      icon: 'mdi-cellphone-arrow-down',
      to: '/local-copies',
      value: 'local-copies',
      show: true,
      domains: ['write'],
    },
    {
      title: 'Papelera',
      icon: 'mdi-delete-outline',
      to: '/trash',
      value: 'trash',
      show: true,
      domains: ['write'],
    },
    {
      title: 'Configuración',
      icon: 'mdi-cog',
      to: '/config',
      value: 'config',
      show: true,
      domains: ['write'],
    },
    {
      title: 'Mi perfil',
      icon: 'mdi-account-circle-outline',
      to: '/profile',
      value: 'profile',
      show: !isGuest,
      domains: ['community'],
    },
    {
      title: 'Actividad',
      icon: 'mdi-newspaper-variant-outline',
      to: '/community/activity',
      value: 'community-activity',
      show: true,
      domains: ['community'],
    },
    {
      title: 'Grupos',
      icon: 'mdi-account-multiple-outline',
      to: '/community/groups',
      value: 'community-groups',
      show: true,
      domains: ['community'],
    },
    {
      title: 'Cápsula',
      icon: 'mdi-cube-outline',
      to: '/community/capsule',
      value: 'community-capsule',
      show: true,
      domains: ['community'],
    },
    {
      title: 'Mis imágenes',
      icon: 'mdi-image-multiple',
      to: '/images',
      value: 'images',
      show: true,
      domains: ['community'],
    },
    {
      title: 'Configuración',
      icon: 'mdi-cog',
      to: '/config',
      value: 'config',
      show: true,
      domains: ['community'],
    },
  ]

  return visibleItems(items, activeDomain)
}

export function buildAppFixedNavigationItems(input: {
  isGuest: boolean
}): AppNavigationItem[] {
  const { isGuest } = input

  return [
    {
      title: 'Iniciar sesión',
      icon: 'mdi-login',
      to: '/login',
      value: 'login',
      show: isGuest,
      domains: 'all' as const,
    },
  ].filter((item) => item.show)
}
